
// materialized path is a generalized technique for storing hierarchies of
// objects in a relational database.
// for more info google "materialized path"
// or https://www.philihp.com/2008/using-a-materialized-path-model-for-trees-within-oltp-databases-part-1.html
// or check out joe celko's "trees & hierarchies in sql" book

export type MaterializedPathNode = string
export type MaterializedPath<T extends MaterializedPathNode> = string
export type MaterializedPathArray<T extends MaterializedPathNode> = T[]

const SEP = '-'

export const materializedPathArray = <T extends MaterializedPathNode>(path: MaterializedPath<T>): MaterializedPathArray<T> => {
	const array = path.split(SEP) as MaterializedPathArray<T>
	for(let n of array) {
		if(n === '') {
			throw new Error('illegal path string with empty nodes')
		}
	}
	return path.split(SEP) as MaterializedPathArray<T>
}

export const materializedPathString = <T extends MaterializedPathNode>(pathArray: MaterializedPathArray<T>): MaterializedPath<T> => {
	for(let n of pathArray) {
		if(n === '') {
			throw new Error('illegal path array with empty nodes')
		}
	}
	return pathArray.join(SEP) as MaterializedPath<T>
}

export const materializedPathDepth = <T extends MaterializedPathNode>(path:  MaterializedPath<T>): number => {
	if(path === '') {
		return 0
	}
	return materializedPathArray(path).length
}

export const materializedPathConcat = <T extends MaterializedPathNode>(path: MaterializedPath<T>, ...nodes: T[]): MaterializedPath<T> => {
	for(let n of nodes) {
		if(n === '') {
			throw new Error('illegal empty nodes passed for concatenation')
		}
	}
	let array: MaterializedPathArray<T> = []
	if(path !== '') {
		array = materializedPathArray(path)
	}
	array.push(...nodes)
	return materializedPathString(array)
}

export const materializedPathConcatIfNotLast = <T extends MaterializedPathNode>(path: MaterializedPath<T>, node: T): MaterializedPath<T> => {
	if(node === '') {
		throw new Error('illegal empty node passed for concatenation')
	}
	let array: MaterializedPathArray<T> = []
	if(path !== '') {
		array = materializedPathArray(path)
	}
	if(array.at(-1) !== node) {
		array.push(node)
	}
	return materializedPathString(array)
}
